import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Plus, Search, Trash2, Network, Sparkles, Loader2, AlertTriangle,
  FileText, Play, Eye, Send, Target, Users, MapPin, Briefcase, Calendar,
  Lightbulb, CheckCircle, XCircle, ChevronRight, BarChart3, Clock,
  Download, RefreshCw, MessageSquare, Zap, ArrowRight, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_COLORS = {
  person: '#007AFF', location: '#34C759', account: '#FF9500',
  asset: '#AF52DE', event: '#FF2D55', organization: '#00C7BE', concept: '#4B5563',
};
const ENTITY_ICONS = {
  person: Users, location: MapPin, account: Briefcase, asset: Sparkles,
  event: Calendar, organization: Network, concept: Lightbulb,
};
const SEVERITY_COLORS = { high: '#FF3B30', medium: '#FF9500', low: '#007AFF' };

const KnowledgeMappingPage = () => {
  const { token } = useAuth();
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [], anomalies: [], metrics: {} });
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Create map
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', data_sources: ['articles', 'tasks', 'pkm_notes', 'webinars'] });

  // Memo
  const [memo, setMemo] = useState(null);
  const [memoLoading, setMemoLoading] = useState(false);

  // Query
  const [queryText, setQueryText] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);

  // View tab
  const [viewTab, setViewTab] = useState('graph');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchMaps(); }, []);

  const fetchMaps = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/knowledge-maps`, { headers });
      setMaps(res.data);
      if (res.data.length > 0 && !selectedMap) {
        selectMap(res.data[0]);
      }
    } catch { /* */ } finally { setLoading(false); }
  };

  const selectMap = async (map) => {
    setSelectedMap(map);
    setGraphLoading(true);
    setMemo(null);
    setQueryResult(null);
    try {
      const res = await axios.get(`${API}/knowledge-maps/${map.map_id}/graph`, { headers });
      setGraphData(res.data);
    } catch { toast.error('Failed to load graph'); }
    finally { setGraphLoading(false); }
  };

  const handleCreate = async () => {
    if (!createForm.name) { toast.error('Name required'); return; }
    try {
      const res = await axios.post(`${API}/knowledge-maps`, createForm, { headers });
      toast.success('Map created');
      setIsCreateOpen(false);
      setCreateForm({ name: '', description: '', data_sources: ['articles', 'tasks', 'pkm_notes', 'webinars'] });
      fetchMaps();
      selectMap(res.data);
    } catch { toast.error('Failed'); }
  };

  const handleExtract = async () => {
    if (!selectedMap) return;
    setExtracting(true);
    try {
      await axios.post(`${API}/knowledge-maps/${selectedMap.map_id}/extract`, {}, { headers });
      toast.success('Extraction started! This may take a moment.');
      // Poll for completion
      const poll = setInterval(async () => {
        const res = await axios.get(`${API}/knowledge-maps/${selectedMap.map_id}`, { headers });
        if (res.data.status !== 'extracting') {
          clearInterval(poll);
          setExtracting(false);
          selectMap(res.data);
          fetchMaps();
          if (res.data.status === 'analyzed') toast.success(`Extracted ${res.data.nodes_count} entities, ${res.data.links_count} links, ${res.data.anomalies_count} anomalies`);
          else if (res.data.status === 'error') toast.error('Extraction failed');
        }
      }, 3000);
      setTimeout(() => { clearInterval(poll); setExtracting(false); }, 120000);
    } catch { toast.error('Failed'); setExtracting(false); }
  };

  const handleAnalyze = async () => {
    if (!selectedMap) return;
    setAnalyzing(true);
    try {
      await axios.post(`${API}/knowledge-maps/${selectedMap.map_id}/analyze`, {}, { headers });
      toast.success('Analysis complete');
      selectMap(selectedMap);
    } catch { toast.error('Failed'); } finally { setAnalyzing(false); }
  };

  const handleMemo = async () => {
    if (!selectedMap) return;
    setMemoLoading(true);
    try {
      const res = await axios.get(`${API}/knowledge-maps/${selectedMap.map_id}/memo`, { headers });
      setMemo(res.data);
    } catch { toast.error('Failed'); } finally { setMemoLoading(false); }
  };

  const handleQuery = async () => {
    if (!queryText.trim() || !selectedMap) return;
    setQueryLoading(true);
    try {
      const res = await axios.post(`${API}/knowledge-maps/${selectedMap.map_id}/query`, { query: queryText, steps: 2 }, { headers });
      setQueryResult(res.data);
    } catch { toast.error('Query failed'); } finally { setQueryLoading(false); }
  };

  const handleFeedback = async (action, nodeId = '', linkId = '') => {
    if (!selectedMap) return;
    try {
      await axios.post(`${API}/knowledge-maps/${selectedMap.map_id}/feedback`, { action, node_id: nodeId, link_id: linkId }, { headers });
      toast.success(action === 'confirm' ? 'Confirmed' : 'Removed');
      selectMap(selectedMap);
    } catch { toast.error('Failed'); }
  };

  const handleSnapshot = async () => {
    if (!selectedMap) return;
    try {
      await axios.post(`${API}/knowledge-maps/${selectedMap.map_id}/snapshot`, {}, { headers });
      toast.success('Snapshot saved');
    } catch { toast.error('Failed'); }
  };

  const handleDownloadPdf = async () => {
    if (!selectedMap) return;
    try {
      const res = await axios.get(`${API}/knowledge-maps/${selectedMap.map_id}/export-pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `knowledge-map-${selectedMap.map_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleShareEmail = async () => {
    if (!selectedMap) return;
    const emails = prompt('Enter email addresses (comma-separated):');
    if (!emails) return;
    const message = prompt('Add a personal message (optional):') || '';
    try {
      const res = await axios.post(`${API}/knowledge-maps/${selectedMap.map_id}/share`, { to_emails: emails.split(',').map(e => e.trim()).filter(Boolean), message }, { headers });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Share failed'); }
  };

  const handleDelete = async (mapId) => {
    try {
      await axios.delete(`${API}/knowledge-maps/${mapId}`, { headers });
      toast.success('Deleted');
      setSelectedMap(null);
      setGraphData({ nodes: [], edges: [], anomalies: [], metrics: {} });
      fetchMaps();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;

  return (
    <div data-testid="knowledge-maps-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Link Analysis Knowledge Maps</h1>
          <p className="text-sm text-[#4B5563]">Automated entity extraction, network analysis, and anomaly detection</p>
        </div>
        <div className="flex gap-2">
          {selectedMap && <Button size="sm" variant="outline" onClick={handleDownloadPdf} data-testid="export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>}
          {selectedMap && <Button size="sm" variant="outline" onClick={handleShareEmail} data-testid="share-email-btn"><Mail className="w-3.5 h-3.5 mr-1" />Share</Button>}
          <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsCreateOpen(true)} data-testid="create-map-btn">
            <Plus className="w-4 h-4 mr-1" />New Map
          </Button>
        </div>
      </div>

      {/* Map Selector */}
      {maps.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {maps.map(m => (
            <button
              key={m.map_id}
              onClick={() => selectMap(m)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${selectedMap?.map_id === m.map_id ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]' : 'border-[#E5E7EB] hover:border-[#002FA7]/30'}`}
              data-testid={`map-selector-${m.map_id}`}
            >
              <Network className="w-3.5 h-3.5" />
              {m.name}
              <Badge variant="secondary" className="text-[8px]">{m.nodes_count}n/{m.links_count}l</Badge>
              {m.anomalies_count > 0 && <Badge className="text-[8px] bg-[#FF3B30]/10 text-[#FF3B30]">{m.anomalies_count}</Badge>}
            </button>
          ))}
        </div>
      )}

      {!selectedMap ? (
        <Card className="border-[#E5E7EB]"><CardContent className="p-12 text-center text-[#9CA3AF]">
          <Network className="w-12 h-12 mx-auto mb-3 text-[#E5E7EB]" />
          <p className="text-sm">Create a knowledge map to get started</p>
          <Button className="mt-3 bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsCreateOpen(true)}>Create Map</Button>
        </CardContent></Card>
      ) : (
        <>
          {/* Actions Bar */}
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={handleExtract} disabled={extracting} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="extract-btn">
                {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                {extracting ? 'Extracting...' : 'Extract Entities'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleAnalyze} disabled={analyzing} data-testid="analyze-btn">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <BarChart3 className="w-3.5 h-3.5 mr-1" />}
                Re-Analyze
              </Button>
              <Button size="sm" variant="outline" onClick={handleMemo} disabled={memoLoading} data-testid="memo-btn">
                {memoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                Decision Memo
              </Button>
              <Button size="sm" variant="outline" onClick={handleSnapshot}><Clock className="w-3.5 h-3.5 mr-1" />Snapshot</Button>
              <div className="ml-auto flex items-center gap-3 text-xs text-[#9CA3AF]">
                <span>{graphData.metrics?.total_nodes || 0} nodes</span>
                <span>{graphData.metrics?.total_links || 0} links</span>
                <span>{graphData.metrics?.communities || 0} clusters</span>
                <span>density: {graphData.metrics?.density || 0}</span>
                <Button size="sm" variant="ghost" className="text-[#FF3B30] h-7" onClick={() => handleDelete(selectedMap.map_id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <Tabs value={viewTab} onValueChange={setViewTab}>
            <TabsList>
              <TabsTrigger value="graph" data-testid="tab-graph"><Network className="w-3.5 h-3.5 mr-1" />Network Graph</TabsTrigger>
              <TabsTrigger value="anomalies" data-testid="tab-anomalies"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Anomalies ({graphData.anomalies?.length || 0})</TabsTrigger>
              <TabsTrigger value="query" data-testid="tab-query"><Search className="w-3.5 h-3.5 mr-1" />Query</TabsTrigger>
              {memo && <TabsTrigger value="memo" data-testid="tab-memo"><FileText className="w-3.5 h-3.5 mr-1" />Memo</TabsTrigger>}
            </TabsList>

            {/* Graph Tab */}
            <TabsContent value="graph">
              <Card className="border-[#E5E7EB]" data-testid="graph-view">
                <CardContent className="p-0">
                  <div style={{ height: 550 }}>
                    {graphLoading ? (
                      <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
                    ) : graphData.nodes.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-[#9CA3AF]">
                        <Network className="w-12 h-12 mb-3 text-[#E5E7EB]" />
                        <p className="text-sm mb-3">No entities yet. Click "Extract Entities" to auto-build the network.</p>
                      </div>
                    ) : (
                      <ReactFlow
                        nodes={graphData.nodes.map(n => ({
                          ...n,
                          data: {
                            ...n.data,
                            label: (
                              <div className="text-center px-1">
                                <div className="text-[10px] font-semibold truncate" style={{ color: ENTITY_COLORS[n.data.entity_type] || '#4B5563' }}>{n.data.label}</div>
                                <div className="text-[8px] text-[#9CA3AF]">{n.data.entity_type}</div>
                                {n.data.is_anomaly && <div className="text-[8px] text-[#FF3B30] font-bold">FLAGGED</div>}
                              </div>
                            ),
                          },
                          style: {
                            ...n.style,
                            background: n.data.confirmed ? 'white' : '#FFF8F0',
                            border: n.data.is_anomaly ? '2px solid #FF3B30' : n.data.confirmed ? `2px solid ${ENTITY_COLORS[n.data.entity_type] || '#E5E7EB'}` : '2px dashed #FF9500',
                            borderRadius: '12px', padding: '4px',
                            boxShadow: n.data.is_anomaly ? '0 0 8px rgba(255,59,48,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                          },
                        }))}
                        edges={graphData.edges}
                        fitView
                      >
                        <Background color="#E5E7EB" gap={24} />
                        <Controls />
                        <MiniMap style={{ height: 80 }} nodeColor={(n) => ENTITY_COLORS[n.data?.entity_type] || '#9CA3AF'} />
                      </ReactFlow>
                    )}
                  </div>

                  {/* Legend */}
                  {graphData.nodes.length > 0 && (
                    <div className="border-t border-[#E5E7EB] px-4 py-2 flex flex-wrap items-center gap-4">
                      {Object.entries(ENTITY_COLORS).map(([type, color]) => {
                        const count = graphData.nodes.filter(n => n.data.entity_type === type).length;
                        if (count === 0) return null;
                        return (
                          <div key={type} className="flex items-center gap-1.5 text-[10px]">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                            <span className="capitalize text-[#4B5563]">{type} ({count})</span>
                          </div>
                        );
                      })}
                      <div className="flex items-center gap-1.5 text-[10px] ml-auto">
                        <div className="w-3 h-3 rounded border-2 border-dashed border-[#FF9500]" />
                        <span className="text-[#9CA3AF]">Unconfirmed</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px]">
                        <div className="w-3 h-3 rounded border-2 border-[#FF3B30]" />
                        <span className="text-[#FF3B30]">Flagged</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Anomalies Tab */}
            <TabsContent value="anomalies">
              <div className="space-y-2" data-testid="anomalies-view">
                {(!graphData.anomalies || graphData.anomalies.length === 0) ? (
                  <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF] text-sm">No anomalies detected. Run extraction and analysis first.</CardContent></Card>
                ) : graphData.anomalies.map((a, i) => (
                  <Card key={a.anomaly_id} className={`border-l-4`} style={{ borderLeftColor: SEVERITY_COLORS[a.severity] || '#9CA3AF' }} data-testid={`anomaly-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="text-[9px]" style={{ backgroundColor: (SEVERITY_COLORS[a.severity] || '#9CA3AF') + '15', color: SEVERITY_COLORS[a.severity] }}>{a.severity}</Badge>
                            <Badge variant="secondary" className="text-[9px] capitalize">{a.type.replace('_', ' ')}</Badge>
                            <h4 className="text-sm font-medium">{a.title}</h4>
                          </div>
                          <p className="text-xs text-[#4B5563] leading-relaxed">{a.description}</p>
                          {a.suggestion && (
                            <div className="flex items-start gap-2 mt-2 p-2 bg-[#F9FAFB] rounded text-xs text-[#002FA7]">
                              <Lightbulb className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>{a.suggestion}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 ml-2">
                          {a.node_ids?.map(nid => (
                            <Button key={nid} size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => handleFeedback('confirm', nid)} data-testid={`confirm-${nid}`}>
                              <CheckCircle className="w-3 h-3 text-[#34C759]" />
                            </Button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Query Tab */}
            <TabsContent value="query">
              <Card className="border-[#E5E7EB]" data-testid="query-view">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Search className="w-4 h-4 text-[#002FA7]" />Query the Network</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-[#4B5563]">Search for entities and explore their neighborhoods within 2 steps.</p>
                  <div className="flex gap-2">
                    <Input value={queryText} onChange={e => setQueryText(e.target.value)} placeholder='e.g., "Show all nodes near Marketing Strategy"' className="flex-1" onKeyDown={e => e.key === 'Enter' && handleQuery()} data-testid="query-input" />
                    <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleQuery} disabled={queryLoading} data-testid="query-btn">
                      {queryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {['Show all nodes near Admin User', 'Find connections to Marketing', 'Who are the key people?'].map((q, i) => (
                      <button key={i} onClick={() => setQueryText(q)} className="text-[10px] px-2 py-1 rounded-full bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]">{q}</button>
                    ))}
                  </div>
                  {queryResult && (
                    <div className="border border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]" data-testid="query-result">
                      <p className="text-xs font-medium text-[#002FA7] mb-2">{queryResult.explanation}</p>
                      {queryResult.center && (
                        <div className="flex items-center gap-2 p-2 bg-white rounded border border-[#002FA7]/20 mb-2">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: (ENTITY_COLORS[queryResult.center.entity_type] || '#4B5563') + '20' }}>
                            <Target className="w-3 h-3" style={{ color: ENTITY_COLORS[queryResult.center.entity_type] }} />
                          </div>
                          <span className="text-xs font-medium">{queryResult.center.label}</span>
                          <Badge variant="secondary" className="text-[8px]">{queryResult.center.entity_type}</Badge>
                        </div>
                      )}
                      <div className="space-y-1 max-h-[250px] overflow-y-auto">
                        {queryResult.results?.filter(r => r.node_id !== queryResult.center?.node_id).map((r, i) => (
                          <div key={i} className="flex items-center gap-2 p-1.5 rounded text-xs hover:bg-white">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ENTITY_COLORS[r.entity_type] || '#4B5563' }} />
                            <span>{r.label}</span>
                            <Badge variant="secondary" className="text-[8px]">{r.entity_type}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Memo Tab */}
            {memo && (
              <TabsContent value="memo">
                <Card className="border-[#E5E7EB]" data-testid="memo-view">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-[#002FA7]" />Decision Memo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2 flex-wrap">
                      <span className="text-xs text-[#9CA3AF]">Top Influencers:</span>
                      {memo.top_influencers?.map((inf, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">{inf}</Badge>
                      ))}
                    </div>
                    <div className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap bg-[#F9FAFB] p-4 rounded-lg border border-[#E5E7EB]" data-testid="memo-content">
                      {memo.memo?.split('\n').map((line, i) => {
                        if (line.startsWith('**') && line.endsWith('**'))
                          return <p key={i} className="font-semibold text-[#0A0A0A] mt-3 mb-1">{line.replace(/\*\*/g, '')}</p>;
                        if (line.startsWith('- '))
                          return <p key={i} className="ml-3">{'  '}· {line.slice(2)}</p>;
                        return <p key={i}>{line}</p>;
                      })}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </>
      )}

      {/* Create Map Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent data-testid="create-map-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Network className="w-5 h-5 text-[#002FA7]" />New Knowledge Map</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder="e.g., Organization Network Analysis" data-testid="map-name-input" /></div>
            <div><Label>Description</Label><Textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} rows={2} /></div>
            <div>
              <Label>Data Sources</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { key: 'articles', label: 'Articles', icon: FileText },
                  { key: 'tasks', label: 'Tasks', icon: Target },
                  { key: 'pkm_notes', label: 'PKM Notes', icon: Lightbulb },
                  { key: 'webinars', label: 'Webinars', icon: Users },
                ].map(s => {
                  const Icon = s.icon;
                  const active = createForm.data_sources.includes(s.key);
                  return (
                    <button key={s.key} onClick={() => setCreateForm({...createForm, data_sources: active ? createForm.data_sources.filter(d => d !== s.key) : [...createForm.data_sources, s.key]})}
                      className={`flex items-center gap-2 p-2 rounded border text-xs ${active ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]' : 'border-[#E5E7EB] text-[#9CA3AF]'}`}>
                      <Icon className="w-3.5 h-3.5" />{s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreate} data-testid="save-map-btn">Create Map</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeMappingPage;
